/**
 * Post-login handoff — the in-app replacement for the web client's
 * `goToControlPanel()`.
 *
 * The web client used to stash the session JWT in a URL fragment and full-page
 * redirect to a separate control panel (`#token=…`). Now that sign-in lives
 * inside the control panel itself, there is no cross-origin handoff: we take
 * the JWT the flow just obtained, validate it against `GET /auth/me`, write the
 * session straight into the auth store, and navigate home (the root route then
 * forwards to the user's role-based landing page).
 *
 * Like `goToControlPanel()`, this is a plain module function (not a hook) so the
 * ported flow can call it from anywhere — SSO callbacks, `setTimeout`, etc.
 */
import { authApi } from '../api/auth.api'
import { useAuthStore } from '../stores/auth.store'
import { tokenStore } from '../api/tokens'
import { ROUTE_PATHS } from '@/config/app.config'
import { router } from '@/app/router/router'

/**
 * Finish sign-in: read the access token captured during the flow, establish the
 * app session from it, then navigate home. On a missing/invalid token it clears
 * the flow state and returns to /login.
 */
export async function completeLogin(): Promise<void> {
  const token = tokenStore.getAccessToken()
  if (!token) {
    await router.navigate({ to: ROUTE_PATHS.login })
    return
  }

  try {
    // Validate the token and resolve the profile, exactly as the old control
    // panel did with the handed-off JWT — then store it as the live session.
    const user = await authApi.me(token)
    useAuthStore.getState().setSessionFromToken({ user, accessToken: token })
    tokenStore.clear()
    await router.navigate({ to: ROUTE_PATHS.root })
  } catch {
    // Token rejected by the API — drop everything and restart sign-in.
    tokenStore.clear()
    useAuthStore.getState().clearSession()
    await router.navigate({ to: ROUTE_PATHS.login })
  }
}
